import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CreatePatientPopup } from "@/Common/Components/Patient/CreatePatientPopup";
import { PdfPageDetails, PdfViewer } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { FileType } from "@/Common/Models/FileType";
import { Guid } from "@/Common/Models/Guid";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { InboundFaxMailboxType } from "@/Common/Models/UI/Enums";
import { DocumentClass } from "@/Common/Resources/Lookups/Enums/DocumentClasses";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { AttachFaxResponse } from "@/Documents/Api/Dto/Response/AttachFaxResponse";
import { InboundFaxesMailboxActions } from "@/Documents/Components/InboundFax/InboundFaxesMailboxActions";
import { InboundFaxIndexingForm } from "@/Documents/Components/InboundFax/InboundFaxIndexingForm";
import { InboundFaxTriageForm } from "@/Documents/Components/InboundFax/InboundFaxTriageForm";
import { InboundFax } from "@/Documents/Models/InboundFax/InboundFax";
import { InboundFaxIndexingFormModel } from "@/Documents/Models/InboundFax/InboundFaxIndexingFormModel";
import { InboundFaxMailboxModel } from "@/Documents/Models/InboundFax/InboundFaxMailboxModel";
import { InboundFaxPageModel } from "@/Documents/Models/InboundFax/InboundFaxPageModel";
import { InboundFaxView } from "@/Documents/Models/UI/InboundFaxEnums";
import { fetchPrescriptionsRenewalCounters } from "@/PrescriptionRenewals/Api/PrescriptionRenewalsClient";

import "./InboundFaxIndexingView.css";

interface InboundFaxIndexingViewProps {
    fax: InboundFax;
    onNextFax: () => void;
    onPreviousFax: () => void;
    onCancel: () => void;
    currentFaxIndex: number;
    totalFaxCount: number;
    showReturnFaxToSenderPopup: (selectedPages?: PdfPageDetails[]) => void;
    onAfterAttachPages: (currentFax: InboundFax) => void;
    pdfViewerSelectedZoomLevelIndex: number;
    onRefreshAndGoNext: () => void;
    onUpdateFaxesClassAndSubClass: (senderFaxNumber: string, classId: string, subClassId: string, hasAssignedClassToFaxSource: boolean) => void;
    onRemoveFaxSourcesClass: (senderFaxNumber: string) => void;
    mailboxModel: InboundFaxMailboxModel;
    onUpdatePendingPage: (faxId: string) => void;
    descriptionsAutoCompleteList: Immutable.List<SearchableItemModelT<string>>;
    sourcesAutoCompleteList: Immutable.List<SearchableItemModelT<string>>;
}

class InboundFaxIndexingViewState {
    public indexingFaxFormData: InboundFaxIndexingFormModel;
    public selectedPages: PdfPageDetails[];
    public documentPagesWithDetails: PdfPageDetails[];
    public rememberDescription: boolean;
    public rememberPatient: boolean;
    public rememberProviders: boolean;
    public rememberSite: boolean;
    public showCreatePatientPopup: boolean;
    public patientCreationModel: IPatientCreationModel;
    public isFormLocked: boolean;
    public isLoadingFax: boolean;

    public static createState(
        props?: InboundFaxIndexingViewProps,
        lockAndPatient?: { isLocked: boolean, patient: Person },
        lockAndDescription?: { isLocked: boolean, description: string },
        lockAndProviders?: { isLocked: boolean, providers: Person[] },
        lockAndSiteCode?: { isLocked: boolean, siteCode: string }
    ): InboundFaxIndexingViewState {
        const state = props
            ? this.fromPropsAndLockedFields(props, lockAndPatient, lockAndDescription, lockAndProviders, lockAndSiteCode)
            : this.getDefaultState();

        return state;
    }

    private static getDefaultState(): InboundFaxIndexingViewState {
        return {
            indexingFaxFormData: new InboundFaxIndexingFormModel(),
            selectedPages: [],
            documentPagesWithDetails: null,
            rememberPatient: false,
            rememberDescription: false,
            rememberProviders: false,
            rememberSite: false,
            showCreatePatientPopup: false,
            patientCreationModel: Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel()),
            isFormLocked: false,
            isLoadingFax: false
        };
    }

    private static fromPropsAndLockedFields(
        props: InboundFaxIndexingViewProps,
        lockAndPatient: { isLocked: boolean, patient: Person },
        lockAndDescription: { isLocked: boolean, description: string },
        lockAndProviders: { isLocked: boolean, providers: Person[] },
        lockAndSiteCode: { isLocked: boolean, siteCode: string }
    ): InboundFaxIndexingViewState {
        const newState = this.fromProps(props);

        if (lockAndPatient?.isLocked) {
            newState.rememberPatient = true;
            newState.indexingFaxFormData.selectedPatient = lockAndPatient.patient;
        }

        if (lockAndDescription?.isLocked) {
            newState.rememberDescription = true;
            newState.indexingFaxFormData.description = lockAndDescription.description;
        }

        if (lockAndProviders?.isLocked) {
            newState.rememberProviders = true;
            newState.indexingFaxFormData.selectedResponsibleProviders = lockAndProviders.providers;
        }

        if (lockAndSiteCode?.isLocked) {
            newState.rememberSite = true;
            newState.indexingFaxFormData.selectedSiteCode = lockAndSiteCode.siteCode;
        }

        if (newState.indexingFaxFormData.isPatientFieldLocked) {
            newState.rememberPatient = true;
        }

        if (newState.indexingFaxFormData.isProvidersFieldLocked) {
            newState.rememberProviders = true;
        }

        if (newState.indexingFaxFormData.isDescriptionFieldLocked) {
            newState.rememberDescription = true;
        }

        if (newState.indexingFaxFormData.isSiteFieldLocked) {
            newState.rememberSite = true;
        }

        return newState;
    }

    private static fromProps(props: InboundFaxIndexingViewProps): InboundFaxIndexingViewState {
        // Default Class Guid to display, If no Class is assigned to a Fax.
        const defaultReportClassLookupId: Guid | string = DocumentTreeClassControl.getDocClassFromLookup(DocumentClass.MedicationAndPrescriptions).LookupId;
        const defaultReportSubClassLookupId: Guid | string = DocumentTreeClassControl.getDefaultSubclassForIndexingFax(DocumentClass.MedicationAndPrescriptions);
        const currentFaxClassLookupId = InboundFax.getClassLookupId(props.fax);
        const currentFaxSubClassLookupId = InboundFax.getSubClassLookupId(props.fax);
        const currentView = props.mailboxModel.currentView;

        let id = props.fax.Id;
        let source: string = props.fax.Source;
        let description: string = "";
        let notes: string = "";
        let documentDate: Date = props.fax.DateReceived;
        let selectedSiteCode: string = sessionStore.getCurrentEmployee().getActiveSite().getId();
        let selectedResponsibleProviders: Person[] = [];
        let isUrgent: boolean = false;
        let selectedClassLookupId: Guid | string = currentFaxClassLookupId ? currentFaxClassLookupId : defaultReportClassLookupId;
        let selectedSubClassLookupId: Guid | string = currentFaxSubClassLookupId ? currentFaxSubClassLookupId : defaultReportSubClassLookupId;
        let selectedPatient: Person = null;
        let shouldAssignClassFaxNumber: boolean = false;
        let shouldAssignSourceFaxNumber: boolean = false;
        let isPatientFieldLocked: boolean = false;
        let isProvidersFieldLocked: boolean = false;
        let isDescriptionFieldLocked: boolean = false;
        let isSiteFieldLocked: boolean = false;

        if (currentView === InboundFaxView.TriageForm) {
            const storedFax: InboundFaxIndexingFormModel = props.mailboxModel.triageFaxes?.firstOrDefault(x => x.id === props.fax.Id);

            notes = storedFax?.notes ?? "";
            isUrgent = storedFax?.isUrgent ?? props.fax.IsUrgent;
        }
        else if (currentView === InboundFaxView.IndexingForm && props.mailboxModel?.faxesForm?.[id]) {
            const deepClonedFaxForm: InboundFaxIndexingFormModel = JSON.parse(JSON.stringify(props.mailboxModel.faxesForm[id]));
            const selectedResponsibleProvidersWithType: Person[] = deepClonedFaxForm?.selectedResponsibleProviders?.filter(p => p)?.map(p => new Person({ ...p })) ?? [];
            const selectedPatientWithType: Person = deepClonedFaxForm?.selectedPatient ? new Person({ ...deepClonedFaxForm.selectedPatient }) : null;

            description = deepClonedFaxForm.description;
            isUrgent = deepClonedFaxForm.isUrgent;
            notes = deepClonedFaxForm.notes;
            documentDate = deepClonedFaxForm.documentDate;
            source = deepClonedFaxForm.source;
            selectedClassLookupId = deepClonedFaxForm.selectedClassLookupId;
            selectedSubClassLookupId = deepClonedFaxForm.selectedSubClassLookupId;
            selectedSiteCode = deepClonedFaxForm.selectedSiteCode;
            selectedResponsibleProviders = selectedResponsibleProvidersWithType;
            selectedPatient = selectedPatientWithType;
            shouldAssignClassFaxNumber = deepClonedFaxForm.shouldAssignClassFaxNumber;
            shouldAssignSourceFaxNumber = deepClonedFaxForm.shouldAssignSourceFaxNumber;
            isPatientFieldLocked = deepClonedFaxForm.isPatientFieldLocked;
            isProvidersFieldLocked = deepClonedFaxForm.isProvidersFieldLocked;
            isDescriptionFieldLocked = deepClonedFaxForm.isDescriptionFieldLocked;
            isSiteFieldLocked = deepClonedFaxForm.isSiteFieldLocked;
        }
        else if (currentView === InboundFaxView.IndexingForm && props.mailboxModel?.faxesForm) {
            const deepClonedFaxForms: { [id: string]: InboundFaxIndexingFormModel } = JSON.parse(JSON.stringify(props.mailboxModel.faxesForm));
            Object.keys(deepClonedFaxForms).forEach(id => {
                const form = deepClonedFaxForms[id];

                if (form.isPatientFieldLocked) {
                    selectedPatient = new Person(form.selectedPatient);
                    isPatientFieldLocked = form.isPatientFieldLocked;
                }

                if (form.isProvidersFieldLocked) {
                    selectedResponsibleProviders = form.selectedResponsibleProviders.map(p => new Person(p));
                    isProvidersFieldLocked = form.isProvidersFieldLocked;
                }

                if (form.isDescriptionFieldLocked) {
                    description = form.description;
                    isDescriptionFieldLocked = form.isDescriptionFieldLocked;
                }

                if (form.isDescriptionFieldLocked) {
                    selectedSiteCode = form.selectedSiteCode;
                    isSiteFieldLocked = form.isSiteFieldLocked;
                }
            });
        }

        return {
            ...this.getDefaultState(),
            indexingFaxFormData: {
                ...new InboundFaxIndexingFormModel(),
                id,
                description,
                isUrgent,
                notes,
                documentDate,
                source,
                selectedClassLookupId,
                selectedSubClassLookupId,
                selectedSiteCode,
                selectedResponsibleProviders,
                selectedPatient,
                shouldAssignClassFaxNumber,
                shouldAssignSourceFaxNumber,
                isPatientFieldLocked,
                isProvidersFieldLocked,
                isDescriptionFieldLocked,
                isSiteFieldLocked
            }
        };
    }
}

class InboundFaxIndexingView extends BaseReactComponent<InboundFaxIndexingViewProps, InboundFaxIndexingViewState> {
    public constructor(props: InboundFaxIndexingViewProps) {
        super(props);
        this.state = InboundFaxIndexingViewState.createState();
    }

    public async componentDidMount(): Promise<void> {
        try {
            this.mutateState(s => s.isLoadingFax = true);

            if (!this.props.fax.AttachmentId) {
                await InboundFaxesMailboxActions.downloadInboundFax(this.props.fax);
            }

            const getInboundFaxPagesResponse = await InboundFaxesMailboxActions.getInboundFaxPages(this.props.fax);
            const newState = InboundFaxIndexingViewState.createState(this.props);

            if (getInboundFaxPagesResponse.IsSuccess) {
                newState.documentPagesWithDetails = getInboundFaxPagesResponse.InboundFaxPages.map(page => ({
                    isAssigned: !!page.AssignedToDocumentDate,
                    isTrashed: !!page.DeletedDate,
                    orientation: page.Orientation,
                    pageNumber: page.PageNumber
                }));
            }

            this.setState(newState);
        }
        catch ({ error }) {
            this.setState(InboundFaxIndexingViewState.createState(this.props));
        }
        finally {
            this.mutateState(s => s.isLoadingFax = false);
        }
    }

    public async componentDidUpdate(prevProps: InboundFaxIndexingViewProps, prevState: InboundFaxIndexingViewState): Promise<void> {
        if (this.props.fax.Id !== prevProps.fax.Id) {
            const patient = prevState.rememberPatient ? prevState.indexingFaxFormData.selectedPatient : null;
            const description = prevState.rememberDescription ? prevState.indexingFaxFormData.description : "";
            const providers = prevState.rememberProviders ? prevState.indexingFaxFormData.selectedResponsibleProviders : [];
            const siteCode = prevState.rememberSite ? prevState.indexingFaxFormData.selectedSiteCode : "";

            try {
                this.mutateState(s => s.isLoadingFax = true);

                if (!this.props.fax.AttachmentId) {
                    await InboundFaxesMailboxActions.downloadInboundFax(this.props.fax);
                }

                const getInboundFaxPagesResponse = await InboundFaxesMailboxActions.getInboundFaxPages(this.props.fax);

                const newState = InboundFaxIndexingViewState.createState(
                    this.props,
                    { isLocked: prevState.rememberPatient, patient },
                    { isLocked: prevState.rememberDescription, description },
                    { isLocked: prevState.rememberProviders, providers },
                    { isLocked: prevState.rememberSite, siteCode }
                );

                if (getInboundFaxPagesResponse.IsSuccess) {
                    newState.documentPagesWithDetails = getInboundFaxPagesResponse.InboundFaxPages.map(page => ({
                        isAssigned: !!page.AssignedToDocumentDate,
                        isTrashed: !!page.DeletedDate,
                        orientation: page.Orientation,
                        pageNumber: page.PageNumber
                    }));
                }

                this.setState(newState);
            }
            catch ({ error }) {
                this.setState(InboundFaxIndexingViewState.createState(this.props));
            }
            finally {
                this.mutateState(s => s.isLoadingFax = false);
            }
        }

        if (this.props.mailboxModel.currentView !== prevProps.mailboxModel.currentView && this.props.mailboxModel.currentView === InboundFaxView.TriageForm) {
            const storedfax = this.props.mailboxModel.triageFaxes?.firstOrDefault(x => x.id === this.props.fax.Id);

            this.mutateState(s => {
                s.indexingFaxFormData.isUrgent = storedfax?.isUrgent ?? this.props.fax.IsUrgent;
                s.indexingFaxFormData.notes = storedfax?.notes;
            });
        }
    }

    public render(): JSX.Element {
        const pdfSourceUrl = !this.state.isLoadingFax
            ? apiSettings.buildAppUrl(FileSelectionHelper.getFetchUrl(this.props.fax?.AttachmentId, FileType.ATTACHMENT))
            : null;

        return (
            <div>
                <div id="posref" className="ws-scrollable inbound-fax-indexing-view">
                    {pdfSourceUrl
                        ? (
                            <PdfViewer
                                key={`${this.props.fax.Id} ${this.props.fax.AttachmentId ?? ""}`}
                                pdfSource={pdfSourceUrl}
                                selectedPages={this.state.selectedPages}
                                onPageSelectionChange={selectedPages => this.mutateState(s => s.selectedPages = selectedPages)}
                                pagesWithDetails={this.state.documentPagesWithDetails}
                                onTrashPage={changedPageDetails => this.displayDeleteIndividualPagePopup(changedPageDetails)}
                                onPageOrientationChange={changedPageDetails => this.updatePageDetails(changedPageDetails)}
                                selectedZoomLevelIndex={this.props.pdfViewerSelectedZoomLevelIndex ?? 5}
                                onZoomLevelChanged={zoomChange => InboundFaxesMailboxActions.setPdfViewerSelectedZoomLevelIndex(zoomChange)}
                                onTrashDocument={() => this.displayDeletePopup()}
                                onClearSelection={() => this.mutateState(s => s.selectedPages = [])}
                                onSelectAllSelection={() => this.selectAllUnmanagedPages()}
                                onPopoutPdfPage={pageNumber => this.generatePdfPreview(pageNumber)}
                                isDeletable
                            />
                        )
                        : (
                            <span className="margin-sm-left margin-sm-top">{cacheStore.GetString("LoadingLabel")}</span>
                        )
                    }
                    {this.state?.showCreatePatientPopup && (
                        <CreatePatientPopup
                            patientCreationModel={this.state?.patientCreationModel}
                            onSave={patient => this.attachPatientToFax(patient)}
                            onCancel={() => this.mutateState(s => s.showCreatePatientPopup = false)}
                        />
                    )}
                    {this.renderForm()}
                </div>

                <footer className="ws-footer inbound-fax-indexing-view-footer">
                    <div>
                        <button
                            className="btn btn-primary"
                            onClick={() => this.props.onPreviousFax()}
                            disabled={this.props.currentFaxIndex === 0}
                        >
                            <span>{"<"}</span>
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => this.props.onNextFax()}
                            disabled={this.props.currentFaxIndex === this.props.totalFaxCount - 1}
                        >
                            <span>{">"}</span>
                        </button>
                        <span>{this.props.currentFaxIndex + 1} {cacheStore.GetString("OfLabel")} {this.props.totalFaxCount}</span>
                    </div>

                    <div>
                        {this.renderFormButtons()}
                    </div>
                </footer>
            </div>
        );
    }

    private renderForm(): JSX.Element {
        return this.props.mailboxModel.currentView === InboundFaxView.IndexingForm
            ? this.renderIndexingForm()
            : this.renderTriageForm();
    }

    private renderIndexingForm(): JSX.Element {
        return (
            <InboundFaxIndexingForm
                defaultDocumentDate={LuxonDateTimeParser.parse(this.props.fax.DateReceived).toLocal().toJSDate()}
                onChange={formData => this.mutateState(s => s.indexingFaxFormData = formData, () => {
                    InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
                })}
                onAssignSourceToFaxNumberChange={newValue =>
                    this.mutateState(
                        s => s.indexingFaxFormData = { ...this.state?.indexingFaxFormData, shouldAssignSourceFaxNumber: newValue }
                        , () => {
                            InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
                        }
                    )
                }
                onAssignClassToFaxNumberChange={newValue => this.checkUncheckClassAndUncheckSubClassIfNecessary(newValue)}
                inboundFaxIndexingFormModel={this.state?.indexingFaxFormData}
                dateReceived={this.props.fax.DateReceived}
                faxNumber={this.props.fax.SenderFaxNumber}
                showReturnFaxToSenderPopup={this.props.showReturnFaxToSenderPopup}
                isPatientFieldLocked={this.state?.rememberPatient}
                isProvidersFieldLocked={this.state?.rememberProviders}
                isDescriptionFieldLocked={this.state?.rememberDescription}
                isSiteFieldLocked={this.state?.rememberSite}
                onTogglePatientFieldLock={isLocked => this.mutateState(s => {
                    s.rememberPatient = isLocked;
                    s.indexingFaxFormData = { ...s.indexingFaxFormData, isPatientFieldLocked: isLocked } as InboundFaxIndexingFormModel;
                }, () => {
                    InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
                })}
                onToggleDescriptionFieldLock={isLocked => this.mutateState(s => {
                    s.rememberDescription = isLocked;
                    s.indexingFaxFormData = { ...s.indexingFaxFormData, isDescriptionFieldLocked: isLocked } as InboundFaxIndexingFormModel;
                }, () => {
                    InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
                })}
                onToggleProvidersFieldLock={isLocked => this.mutateState(s => {
                    s.rememberProviders = isLocked;
                    s.indexingFaxFormData = { ...s.indexingFaxFormData, isProvidersFieldLocked: isLocked } as InboundFaxIndexingFormModel;
                }, () => {
                    InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
                })}
                onToggleSiteFieldLock={isLocked => this.mutateState(s => {
                    s.rememberSite = isLocked;
                    s.indexingFaxFormData = { ...s.indexingFaxFormData, isSiteFieldLocked: isLocked } as InboundFaxIndexingFormModel;
                }, () => {
                    InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
                })}
                onShowCreatePatientPopup={showPopUp => this.mutateState(s => s.showCreatePatientPopup = showPopUp)}
                selectedPages={this.state.selectedPages.map(page => page.pageNumber)}
                reportClassLookupId={this.props.fax.ReportClassLookupId}
                reportSubClassLookupId={this.props.fax.ReportSubClassLookupId}
                descriptionsAutoCompleteList={this.props.descriptionsAutoCompleteList}
                sourcesAutoCompleteList={this.props.sourcesAutoCompleteList}
                faxSourceClassLookupId={this.props.fax.FaxSourceClassLookupId}
                onRemoveFaxSourcesClass={senderFaxNumber => this.props.onRemoveFaxSourcesClass(senderFaxNumber)}
            />
        );
    }

    private renderTriageForm(): JSX.Element {
        return (
            <InboundFaxTriageForm
                onChange={formData => this.onChangeTriageForm(formData)}
                onClassClick={(classId, subClassId) => this.onClassClick(classId, subClassId)}
                inboundFaxIndexingFormModel={this.state?.indexingFaxFormData}
                onShowReturnFaxToSenderPopup={this.props.showReturnFaxToSenderPopup}
                reportClassLookupId={InboundFax.getClassLookupId(this.props.fax)}
                reportSubClassLookupId={InboundFax.getSubClassLookupId(this.props.fax)}
                faxNotes={this.props.fax.FaxNotes}
            />
        );
    }

    private renderFormButtons(): JSX.Element {
        return this.props.mailboxModel.currentView === InboundFaxView.IndexingForm
            ? this.renderIndexingFormButtons()
            : this.renderTriageFormButtons();
    }

    private renderTriageFormButtons(): JSX.Element {
        return (
            <div>
                <ButtonControl
                    onClick={() => this.displayDeletePopup()}
                    label={cacheStore.GetString("DeleteLabel")}
                    className="btn btn-danger"
                />
                {this.renderCancelButton()}
                <ButtonControl
                    onClick={() => this.onShowIndexingForm()}
                    label={cacheStore.GetString("AssignLabel")}
                    className="btn btn-primary"
                    disabled={!this.props.fax.AttachmentId}
                />

                <ButtonControl
                    onClick={async () => await this.saveAndNext()}
                    label={cacheStore.GetString("SaveAndNextButton")}
                    className="btn btn-primary"
                    disabled={!this.props.fax.AttachmentId && this.isTriageFormDirty()}
                />
            </div>
        );
    }

    private renderIndexingFormButtons(): JSX.Element {
        return (
            <div>
                <div style={{ marginRight: 5 }}>
                    <CheckboxControl
                        label={cacheStore.GetString("UrgentLabel")}
                        checked={this.state?.indexingFaxFormData?.isUrgent || false}
                        onChange={(isChecked: boolean) =>
                            this.mutateState(
                                s => s.indexingFaxFormData = { ...this.state?.indexingFaxFormData, isUrgent: isChecked },
                                () => {
                                    InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
                                }
                            )
                        }
                    />
                </div>
                {this.renderCancelButton()}

                {cacheStore.ClinicSettings.EnableFaxCoverSheet && (
                    <ButtonControl
                        onClick={() => this.props.showReturnFaxToSenderPopup(this.state.selectedPages)}
                        label={cacheStore.GetString("ReturnToSenderLabel")}
                        variant="neutral"
                    />
                )}

                {cacheStore.ClinicSettings.EnableInboundFaxTriage && (
                    <ButtonControl
                        onClick={() => InboundFaxesMailboxActions.showFaxTriageForm(this.props.fax)}
                        label={cacheStore.GetString("TriageLabel")}
                        className="btn btn-default"
                        disabled={!this.props.fax.AttachmentId}
                    />
                )}
                <ButtonControl
                    disabled={!this.props.fax.AttachmentId || !this.getIsFormValid() || this.state.isFormLocked}
                    onClick={async () => await this.attachFax()}
                    label={cacheStore.GetString("AttachLabel")}
                    className="btn btn-primary"
                    dataCy="fax-indexing-attach"
                />
                {this.isLoggedInUserResponsibleProvider() && sessionStore.canApproveDocument() && (
                    <ButtonControl
                        disabled={!this.props.fax.AttachmentId || !this.getIsFormValid() || this.state.isFormLocked}
                        onClick={() => this.onAttachAndApproveClick()}
                        label={cacheStore.GetString("AttachAndApproveLabel")}
                        className="btn btn-primary"
                    />
                )}
            </div>
        );
    }

    private renderCancelButton(): JSX.Element {
        return (
            <ButtonControl
                onClick={() => this.onCancelClickHandler()}
                label={cacheStore.GetString("CancelLabel")}
                className="btn btn-default"
            />
        );
    }

    private isTriageFormDirty(): boolean {
        return !this.props.mailboxModel.triageFaxes?.any(x => x.id === this.props.fax.Id);
    }

    private onCancelClickHandler(): void {
        InboundFaxesMailboxActions.clearTriageFaxInfos();
        this.props.onCancel();
    }

    private setToDocumentStore(): void {
        InboundFaxesMailboxActions.storeTriageFaxInfo(this.state.indexingFaxFormData, this.props.mailboxModel.triageFaxes);
    }

    private onChangeTriageForm(data: InboundFaxIndexingFormModel): void {
        const isUrgentAndNotesEdited = this.props.fax.IsUrgent !== data.isUrgent || !IsNullOrWhiteSpace(data.notes);
        this.mutateState(s => {
            s.indexingFaxFormData = data;
        }, () => isUrgentAndNotesEdited ? this.setToDocumentStore() : InboundFaxesMailboxActions.removeTriageFaxInfo(this.props.fax.Id, this.props.mailboxModel.triageFaxes));
    }

    private onClassClick(classId: Guid | string, subClassId: Guid | string): void {
        this.mutateState(
            s => {
                s.indexingFaxFormData.selectedClassLookupId = classId;
                s.indexingFaxFormData.selectedSubClassLookupId = subClassId;
            },
            async () => await this.saveAndNext()
        );
    }

    private async saveAndNext(): Promise<void> {
        const result = await InboundFaxesMailboxActions.updateInboundFaxAndSource(this.state.indexingFaxFormData);

        if (result?.IsSuccess) {
            InboundFaxesMailboxActions.removeTriageFaxInfo(this.props.fax.Id, this.props.mailboxModel.triageFaxes);
            this.props.onUpdateFaxesClassAndSubClass(
                this.props.fax.SenderFaxNumber,
                this.state.indexingFaxFormData.selectedClassLookupId.toString(),
                this.state.indexingFaxFormData.selectedSubClassLookupId.toString(),
                this.state.indexingFaxFormData.shouldAssignClassFaxNumber
            );
            this.props.onRefreshAndGoNext();
            InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(documentsStore.InboundFaxInboxModel.filteringSettings);
        }
    }

    private async attachFax(isApproved: boolean = false): Promise<void> {
        this.mutateState(
            s => s.isFormLocked = true,
            async () => {
                let selectedPages = [...this.state.selectedPages];
                if (selectedPages.length === 0) {
                    selectedPages = this.getAllUnmanagedPages(this.state.documentPagesWithDetails);
                }

                const result = await InboundFaxesMailboxActions.attachFax(this.props.fax.Id, this.state.indexingFaxFormData, selectedPages, isApproved);
                if (result?.IsSuccess) {
                    this.updateDocumentAfterAttach(result);
                }
                else {
                    this.mutateState(s => s.isFormLocked = false);
                }
            }
        );
    }

    private toCheckFormRememberFieldsAndReset(pendingPageCount: number, currentFax: InboundFax): void {
        this.props.onAfterAttachPages(currentFax);
        if (pendingPageCount > 0) {
            this.mutateState(s => {
                s.indexingFaxFormData.selectedPatient = this.state?.rememberPatient ? this.state.indexingFaxFormData.selectedPatient : null;
                s.indexingFaxFormData.selectedResponsibleProviders = this.state?.rememberProviders ? this.state.indexingFaxFormData.selectedResponsibleProviders : [];
                s.indexingFaxFormData.description = this.state?.rememberDescription ? this.state.indexingFaxFormData.description : "";
                s.indexingFaxFormData.selectedSiteCode = this.state?.rememberSite ? this.state.indexingFaxFormData.selectedSiteCode : sessionStore.getCurrentEmployee().getActiveSite().getId();
                s.indexingFaxFormData.notes = "";
                s.indexingFaxFormData.shouldAssignClassFaxNumber = s.indexingFaxFormData.shouldAssignSourceFaxNumber = false;
                s.isFormLocked = false;
            });
        }
        else {
            this.mutateState(s => s.isFormLocked = false);
        }
    }

    private checkUncheckClassAndUncheckSubClassIfNecessary(shouldAssign: boolean): void {
        const nextFormData = { ...this.state?.indexingFaxFormData, shouldAssignClassFaxNumber: shouldAssign };
        this.mutateState(s => s.indexingFaxFormData = nextFormData, () => {
            InboundFaxesMailboxActions.saveCurrentForm(this.props.mailboxModel.faxesForm, this.state.indexingFaxFormData);
        });
    }

    private generatePdfPreview(pageNumber: number): void {
        const pagenumbers = this.state.selectedPages.any(e => e.pageNumber === pageNumber)
            ? this.state.selectedPages.map(e => e.pageNumber)
            : [pageNumber];
        InboundFaxesMailboxActions.GeneratePdfPreview(this.props.fax.Id, pagenumbers, InboundFaxMailboxType.Inbox);
    }

    private getIsFormValid(): boolean {
        return !!this.state?.indexingFaxFormData?.documentDate
            && !!this.state?.indexingFaxFormData?.selectedPatient
            && !!this.state?.indexingFaxFormData?.selectedClassLookupId
            && !!this.state?.indexingFaxFormData?.selectedSubClassLookupId
            && !!this.state?.indexingFaxFormData?.selectedSiteCode;
    }

    private async attachPatientToFax(patientModel: IPatientCreationModel): Promise<void> {
        const response = await InboundFaxesMailboxActions.createPatient(patientModel);

        if (response.Individual) {
            this.mutateState(s => {
                s.showCreatePatientPopup = false;
                s.indexingFaxFormData.selectedPatient = Person.FromIndividual(response.Individual);
                s.indexingFaxFormData.selectedPatient.ContactFacetId = response.PatientContactFacetId;
            });
        }
    }

    private async updatePageDetails(pageDetails: PdfPageDetails): Promise<void> {
        const pageToUpdate: InboundFaxPageModel = {
            ...(new InboundFaxPageModel()),
            InboundFaxId: this.props.fax.Id,
            PageNumber: pageDetails.pageNumber,
            Orientation: pageDetails.orientation,
            DeletedDate: pageDetails.isTrashed ? new Date() : null
        };

        await InboundFaxesMailboxActions.updateInboundFaxPages([pageToUpdate]);

        const details = [...this.state.documentPagesWithDetails.filter(p => p.pageNumber !== pageDetails.pageNumber), pageDetails];
        this.mutateState(s => s.documentPagesWithDetails = details);
    }

    private displayDeletePopup(): void {
        const faxId = this.props.fax.Id;

        sessionStore.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: cacheStore.GetString("AreYouSureWantToDeleteThisFax"),
            okLabel: cacheStore.GetString("DeleteLabel"),
            showCancel: true,
            okVariant: "error",
            showCloseX: true,
            okAction: () => this.trashFaxFromViewer(faxId)
        });
    }

    private displayDeleteIndividualPagePopup(pageDetails: PdfPageDetails): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: cacheStore.GetString("AreYouSureWantToDeleteThisPage"),
            okLabel: cacheStore.GetString("DeleteLabel"),
            showCancel: true,
            okVariant: "error",
            showCloseX: true,
            okAction: () => this.deleteIndividualPage(pageDetails)
        });
    }

    private async deleteIndividualPage(pageDetails: PdfPageDetails): Promise<void> {
        await this.updatePageDetails(pageDetails);
        this.updatePageSelections(pageDetails);
        if (this.IsAllPagesManaged()) {
            this.trashFaxFromViewer(this.props.fax.Id);
        }

        this.props.onUpdatePendingPage(this.props.fax.Id);
    }

    private updatePageSelections(pageDetails: PdfPageDetails): void {
        this.mutateState(s => s.selectedPages = this.state.selectedPages.filter(p => p.pageNumber !== pageDetails.pageNumber));
    }

    private IsAllPagesManaged(): boolean {
        return this.state.documentPagesWithDetails?.every(p => p.isAssigned || p.isTrashed) ?? false;
    }

    private async trashFaxFromViewer(faxId: string): Promise<void> {
        await InboundFaxesMailboxActions.deleteSingleFax(faxId);
        InboundFaxesMailboxActions.removeTriageFaxInfo(this.props.fax.Id, this.props.mailboxModel.triageFaxes);
        this.props.onRefreshAndGoNext();
    }

    private isLoggedInUserResponsibleProvider(): Person | false {
        return this.state?.indexingFaxFormData?.selectedResponsibleProviders?.find(provider => provider.ContactId === sessionStore.getCurrentEmployee().ContactId) ?? false;
    }

    private onAttachAndApproveClick(): void {
        this.attachFax(true);
    }

    private updateDocumentPagesWithDetailsAfterAssign(then: () => void): void {
        InboundFaxesMailboxActions.clearTriageFaxInfos();
        const pageWithDetails = [...this.state.documentPagesWithDetails];
        for (let page of pageWithDetails) {
            if (this.state.selectedPages.some(selectedPage => selectedPage.pageNumber === page.pageNumber)) {
                page.isAssigned = true;
            }
        }
        this.mutateState(
            s => {
                s.documentPagesWithDetails = pageWithDetails;
                s.selectedPages = [];
            },
            () => then()
        );
    }

    private selectAllUnmanagedPages(): void {
        const selectedPages = this.getAllUnmanagedPages(this.state.documentPagesWithDetails);
        this.mutateState(s => s.selectedPages = selectedPages.sortByProperties(p => p.pageNumber));
    }

    private getAllUnmanagedPages(pdfPages: PdfPageDetails[]): PdfPageDetails[] {
        return pdfPages.filter(p => !p.isAssigned && !p.isTrashed);
    }

    private onShowIndexingForm(): void {
        this.mutateState(s => {
            s.indexingFaxFormData.isUrgent = false;
            s.indexingFaxFormData.notes = "";
        }, () => {
            InboundFaxesMailboxActions.showFaxIndexingForm(this.props.fax);
        });
    }

    private isPrescriptionRenewalEnable(): boolean {
        return (
            cacheStore.ClinicSettings.EnablePrescriptionRenewal
            && this.state.indexingFaxFormData.selectedClassLookupId === cacheStore.FindLookupByLookupCode(DocumentClass.MedicationAndPrescriptions).LookupID
        );
    }

    private async updateDocumentAfterAttach(result: AttachFaxResponse): Promise<void> {
        const senderFaxNumber = this.props.fax.SenderFaxNumber;
        const newClassId = this.state.indexingFaxFormData.shouldAssignClassFaxNumber ?
            this.state.indexingFaxFormData.selectedClassLookupId.toString() :
            this.props.fax.ReportClassLookupId;
        const newSubClassId = this.state.indexingFaxFormData.shouldAssignClassFaxNumber ?
            this.state.indexingFaxFormData.selectedSubClassLookupId.toString() :
            this.props.fax.ReportSubClassLookupId;
        const currentFax = {
            ...this.props.fax,
            DeletedPageCount: result.DeletedPageCount,
            PendingPageCount: result.PendingPageCount,
            ReportClassLookupId: newClassId,
            ReportSubClassLookupId: newSubClassId
        };

        this.updateDocumentPagesWithDetailsAfterAssign(
            () => this.toCheckFormRememberFieldsAndReset(result.PendingPageCount, currentFax)
        );
        this.props.onUpdateFaxesClassAndSubClass(senderFaxNumber, newClassId, newSubClassId, this.state.indexingFaxFormData.shouldAssignClassFaxNumber);

        // it might user attach fax ot itself thats why we update document counter
        await documentsStore.DocumentActions.setUnmanagedDocumentCount(documentsStore.DocumentsModel);
        await InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(documentsStore.InboundFaxInboxModel.filteringSettings);
        // update Prescription renewal tab counters
        if (this.isPrescriptionRenewalEnable())
            fetchPrescriptionsRenewalCounters();
    }
}

export { InboundFaxIndexingView };
